import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  LayoutGrid,
  ClipboardList,
  Radar,
  RefreshCcw,
  Loader2,
  ArrowRight,
  Lock,
  Copy,
  Sparkles,
  Eye,
  PenLine,
  X,
  TrendingUp,
  Search,
  Atom,
  ClipboardCheck,
} from "lucide-react";
import clsx from "clsx";
import { api, ApiError } from "../api";
import { Tabs, EmptyState, useToast } from "../ui";
import { useT } from "../i18n";
import EvidenceReviewTab, { type EvidenceCandidate } from "./EvidenceReviewTab";
import { ReleasedClaimTile, groupByArea, type ReleasedClaim } from "./releasedClaims";
import {
  StatusPill,
  TopicClassBadge,
  RubrikHeading,
  Eyebrow,
  CardShell,
  MoACard,
  benefitAreaLabel,
  TrendMatchBadge,
  CertOverlapBadges,
  type MoACardData,
} from "./blog/shared";

// ─────────────────────────────────────────────────────────────────────────────
// Blog-Cockpit — der Kommandostand der Blog-Engine. Drei Sichten in EINER
// Karten-Sprache (Doktrin docs/blog-engine-konzept.md):
//   1. Backlog       — Such-Intent-Topics je Rubrik (Basis + Kür)
//   2. Claims        — die released Evidenz (evi_feed, P3b)
//   3. Trend-Inbox   — vom Radar erkannte Trends (watch/write/dismiss)
// Detail (Topic) läuft auf eigener Route /blog/topic/:id — NIE als Modal.
// ─────────────────────────────────────────────────────────────────────────────

interface BacklogTopic {
  id: string;
  search_intent: string;
  topic_class: string;
  status: string;
  evi_claim_count: number;
  format_hint: string | null;
  language: string;
  title: string | null;
  slug: string | null;
  article_id: string | null;
  trend_signal_id: string | null;
  blocked_reason: string | null;
  priority: number;
  notes: string | null;
  // N1/N10 (Mig 0124) — null bis der Topic-Universe-Loader gelaufen ist.
  primary_keyword?: string | null;
  article_type?: string | null;
  duplicate_of_id?: string | null;
}
interface RubrikGroup {
  benefit_area: string;
  topics: BacklogTopic[];
  count: number;
}
interface BacklogResponse {
  benefit_areas: RubrikGroup[];
  total_topics: number;
}


interface Trend {
  id: string;
  term: string;
  headline: string | null;
  framing_md: string | null;
  benefit_area: string | null;
  evi_groundable: boolean;
  status: string;
  momentum_note: string | null;
  source: string;
  source_urls: string[];
  promoted_topic_id: string | null;
  write_eligible: boolean;
  claim_match: string | null;
  cert_overlap: string[] | null;
}
interface TrendsResponse {
  trends: Trend[];
  total: number;
}

interface MoACardsResponse {
  moa_cards: MoACardData[];
  total: number;
}

type TabKey = "backlog" | "claims" | "moa" | "trends" | "evidence";

export default function BlogDashboardPage() {
  const t = useT();
  const [backlog, setBacklog] = useState<BacklogResponse | null>(null);
  const [claims, setClaims] = useState<ReleasedClaim[]>([]);
  const [moaCards, setMoaCards] = useState<MoACardData[]>([]);
  const [trends, setTrends] = useState<Trend[]>([]);
  const [evidence, setEvidence] = useState<EvidenceCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  // Aktiver Tab lebt in der URL (?tab=…), damit ein Rücksprung aus einer
  // Detailseite (Claim/Topic/Trend) auf der Rubrik landet, aus der der Nutzer
  // kam — nicht immer auf dem Default. Ungültige/fehlende Werte → backlog.
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab: TabKey =
    rawTab === "claims" || rawTab === "moa" || rawTab === "trends" || rawTab === "evidence"
      ? rawTab
      : "backlog";
  const setTab = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, moa, tr, ev] = await Promise.all([
        api.get<BacklogResponse>("/api/blog-dashboard/backlog"),
        api.get<ReleasedClaim[]>("/api/evi-claims"),
        api.get<MoACardsResponse>("/api/blog-dashboard/moa-cards"),
        api.get<TrendsResponse>("/api/blog-dashboard/trends"),
        api.get<EvidenceCandidate[]>("/api/evidence-review"),
      ]);
      setBacklog(b);
      setClaims(c);
      setMoaCards(moa.moa_cards);
      setTrends(tr.trends);
      setEvidence(ev);
    } catch (e) {
      console.error("load blog-dashboard failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const topics = backlog?.benefit_areas.flatMap((g) => g.topics) ?? [];
    return {
      topics: backlog?.total_topics ?? 0,
      published: topics.filter((x) => x.status === "published").length,
      blocked: topics.filter((x) => !!x.blocked_reason).length,
      toWrite: trends.filter((x) => x.status === "write").length,
    };
  }, [backlog, trends]);

  // Badge = Anzahl freigegebener Evidenz-Karten (Release-Gate, Evidence-Board P3).
  const released = evidence.filter((c) => c.release_status === "released").length;

  const tabItems = [
    { id: "backlog", label: t.blog.tabs.backlog, badge: stats.topics || undefined, icon: LayoutGrid },
    { id: "claims", label: t.blog.tabs.claims, badge: claims.length || undefined, icon: ClipboardList },
    { id: "moa", label: t.blog.tabs.moa, badge: moaCards.length || undefined, icon: Atom },
    { id: "evidence", label: t.blog.tabs.evidence, badge: released || undefined, icon: ClipboardCheck },
    { id: "trends", label: t.blog.tabs.trends, badge: trends.length || undefined, icon: Radar },
  ];

  if (loading && !backlog) {
    return (
      <div className="mx-auto max-w-6xl p-6 flex items-center gap-2 text-text-3">
        <Loader2 className="h-4 w-4 animate-spin" /> {t.blog.loading}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            {t.blog.title}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-3">
            <Stat n={stats.topics} label={t.blog.stats.topics} />
            <Dot />
            <Stat n={stats.published} label={t.blog.stats.published} tone="success" />
            <Dot />
            <Stat n={stats.blocked} label={t.blog.stats.blocked} tone={stats.blocked ? "warning" : undefined} />
            <Dot />
            <Stat n={stats.toWrite} label={t.blog.stats.trendsToWrite} tone={stats.toWrite ? "accent" : undefined} />
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          title={t.blog.reload}
          className="shrink-0 rounded-control p-2 hover:bg-surface-3"
        >
          <RefreshCcw className={clsx("h-4 w-4", loading && "animate-spin")} />
        </button>
      </div>

      <Tabs
        className="mb-5"
        value={tab}
        onChange={setTab}
        items={tabItems.map((it) => ({
          id: it.id,
          badge: it.badge,
          label: (
            <span className="inline-flex items-center gap-1.5">
              <it.icon className="h-4 w-4" />
              {it.label}
            </span>
          ),
        }))}
      />

      {tab === "backlog" && <BacklogTab backlog={backlog} />}
      {tab === "claims" && <ClaimsTab claims={claims} />}
      {tab === "moa" && <MoATab cards={moaCards} />}
      {tab === "evidence" && (
        <EvidenceReviewTab
          candidates={evidence}
          onUpdated={(row) => setEvidence((ev) => ev.map((c) => (c.claim_id === row.claim_id ? row : c)))}
        />
      )}
      {tab === "trends" && <TrendsTab trends={trends} onChanged={load} />}
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone?: "success" | "warning" | "accent" }) {
  return (
    <span>
      <span
        className={clsx(
          "font-numeric font-semibold tabular-nums",
          tone === "success" && "text-success",
          tone === "warning" && "text-warning",
          tone === "accent" && "text-accent-600 dark:text-accent-300",
          !tone && "text-text"
        )}
      >
        {n}
      </span>{" "}
      {label}
    </span>
  );
}
function Dot() {
  return <span className="text-text-3/50">·</span>;
}

// Filter-Pill — dieselbe Vokabel wie die Trend-Inbox-Filter, geteilt von der
// Rubrik-Filterleiste des Claims-Archivs, der MoA-Galerie und des Evidenz-Reviews.
export function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "rounded-chip px-2.5 py-1 text-xs font-medium transition-colors",
        active ? "bg-accent-500 text-white" : "bg-surface-2 text-text-2 hover:bg-surface-3"
      )}
    >
      {children}
    </button>
  );
}

// ═══ Backlog ══════════════════════════════════════════════════════════════════
function BacklogTab({ backlog }: { backlog: BacklogResponse | null }) {
  const t = useT();
  const navigate = useNavigate();
  const groups = backlog?.benefit_areas ?? [];

  if (groups.length === 0) {
    return (
      <EmptyState
        icon={<LayoutGrid className="h-6 w-6" />}
        title={t.blog.backlog.empty}
        hint={t.blog.backlog.emptyHint}
      />
    );
  }

  return (
    <div className="space-y-7">
      <p className="text-sm text-text-2">{t.blog.backlog.intro}</p>
      {groups.map((g) => (
        <section key={g.benefit_area}>
          <RubrikHeading title={benefitAreaLabel(g.benefit_area)} count={g.count} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {g.topics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} onOpen={() => navigate(`/blog/topic/${topic.id}`)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function TopicCard({ topic, onOpen }: { topic: BacklogTopic; onOpen: () => void }) {
  const t = useT();
  const blocked = !!topic.blocked_reason;
  const noEvi = topic.evi_claim_count === 0;
  const typeLabel = topic.article_type
    ? (t.blog.articleType as Record<string, string>)[topic.article_type] ?? topic.article_type
    : null;
  const keyword =
    topic.primary_keyword && topic.primary_keyword !== topic.search_intent ? topic.primary_keyword : null;
  return (
    <CardShell interactive onClick={onOpen} className="group flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <StatusPill status={topic.status} />
          <TopicClassBadge topicClass={topic.topic_class} />
          {typeLabel && (
            <span className="rounded-chip bg-surface-2 px-1.5 py-0.5 text-[0.68rem] font-medium text-text-2">
              {typeLabel}
            </span>
          )}
        </div>
        <span className="font-mono text-[0.68rem] uppercase text-text-3">{topic.language}</span>
      </div>

      <div className="font-display text-[1.02rem] font-semibold leading-snug text-text">
        {topic.search_intent}
      </div>
      {keyword && (
        <div className="-mt-1.5 truncate text-xs text-text-3" title={keyword}>
          {t.blog.card.keyword}: <span className="text-text-2">{keyword}</span>
        </div>
      )}
      {blocked && (
        <p className="line-clamp-2 text-xs text-warning" title={topic.blocked_reason ?? undefined}>
          {topic.blocked_reason}
        </p>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1 text-xs text-text-3">
        <span className="inline-flex items-center gap-2">
          <span
            className={clsx(
              "inline-flex items-center gap-1 rounded-chip px-1.5 py-0.5",
              noEvi ? "bg-warning-soft text-warning" : "bg-surface-2 text-text-2"
            )}
          >
            <Sparkles className="h-3 w-3" />
            {noEvi ? t.blog.card.noEvidence : t.blog.card.eviClaims(topic.evi_claim_count)}
          </span>
          {blocked && (
            <span className="inline-flex items-center gap-1 text-warning">
              <Lock className="h-3 w-3" /> {t.blog.card.blocked}
            </span>
          )}
          {topic.duplicate_of_id && (
            <span
              className="inline-flex items-center gap-1 rounded-chip bg-warning-soft px-1.5 py-0.5 text-warning"
              title={t.blog.card.duplicateHint}
            >
              <Copy className="h-3 w-3" /> {t.blog.card.duplicate}
            </span>
          )}
        </span>
        <ArrowRight className="h-4 w-4 text-text-3 transition-transform group-hover:translate-x-0.5 group-hover:text-accent-500" />
      </div>
    </CardShell>
  );
}

// ═══ Claims (released) ═══════════════════════════════════════════════════════
// P3b: the released evidence feed (GET /api/evi-claims → evi_feed) — only cards released on
// the Evidence board; drop/safety never appear. Card SVG inline (like the Evidence tab), each
// card → /claims/:claim_id, "Proof" → the card on the Evidence board.
function ClaimsTab({ claims }: { claims: ReleasedClaim[] }) {
  const t = useT();
  const [filter, setFilter] = useState<string>("all");

  // Distinct effect areas (first-appearance order) as filter options.
  const areas = useMemo(() => groupByArea(claims).map((g) => g.area), [claims]);
  const shown = filter === "all" ? claims : claims.filter((c) => (c.effect_area_en || "Other") === filter);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-text-2">{t.blog.claims.intro}</p>
        <Link
          to="/blog/dashboard?tab=evidence"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-border px-3 py-1.5 text-sm text-text-2 hover:bg-surface-3"
        >
          <ClipboardCheck className="h-4 w-4" /> {t.blog.tabs.evidence}
        </Link>
      </div>

      {claims.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title={t.blog.claims.empty}
          hint={t.blog.claims.emptyHint}
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-1.5">
            <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
              {t.blog.claims.all}
            </FilterPill>
            {areas.map((a) => (
              <FilterPill key={a} active={filter === a} onClick={() => setFilter(a)}>
                {a}
              </FilterPill>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-released-grid>
            {shown.map((c) => (
              <ReleasedClaimTile key={c.claim_id} claim={c} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══ MoA-Galerie ══════════════════════════════════════════════════════════════
// Die Mechanismus-Karten-Ebene neben dem Claims-Archiv (mirror ClaimsTab): alle
// aktiven MoA-Karten je Rubrik gruppiert, jede mit Fußnoten + Provenance sichtbar.
function MoATab({ cards }: { cards: MoACardData[] }) {
  const t = useT();
  const [filter, setFilter] = useState<string>("all");

  // Distinct Rubriken (Erst-Auftritts-Reihenfolge) als Filter-Optionen.
  const rubrics = useMemo(() => {
    const seen: string[] = [];
    for (const c of cards) {
      if (c.rubric && !seen.includes(c.rubric)) seen.push(c.rubric);
    }
    return seen;
  }, [cards]);

  const shown = filter === "all" ? cards : cards.filter((c) => c.rubric === filter);

  return (
    <div>
      <p className="mb-4 text-sm text-text-2">{t.blog.moa.intro}</p>
      {cards.length === 0 ? (
        <EmptyState
          icon={<Atom className="h-6 w-6" />}
          title={t.blog.moa.empty}
          hint={t.blog.moa.emptyHint}
        />
      ) : (
        <>
          {/* Rubrik-Filter (dieselbe Pill-Sprache wie das Claims-Archiv). */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
              {t.blog.moa.all}
            </FilterPill>
            {rubrics.map((r) => (
              <FilterPill key={r} active={filter === r} onClick={() => setFilter(r)}>
                {benefitAreaLabel(r)}
              </FilterPill>
            ))}
          </div>

          {/* Eine flache, responsive Galerie über alle (gefilterten) MoA-Karten. */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {shown.map((c) => (
              <MoACard key={c.id} card={c} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══ Trend-Inbox ══════════════════════════════════════════════════════════════
function TrendsTab({ trends, onChanged }: { trends: Trend[]; onChanged: () => void }) {
  const t = useT();
  const toast = useToast();
  const [filter, setFilter] = useState<"all" | "watch" | "write" | "dismissed">("all");
  const [refreshing, setRefreshing] = useState(false);
  const [focus, setFocus] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const shown = trends.filter((x) => filter === "all" || x.status === filter);

  // A radar run is idempotent per term (an existing trend is updated, not
  // duplicated), so a broad refresh often returns "0 new" — that is expected,
  // not a bug. New trends come from a NEW focus keyword or from time passing.
  const refresh = async (focusArg?: string) => {
    const kw = (focusArg ?? "").trim();
    setRefreshing(true);
    try {
      const res = await api.post<{
        created?: number;
        updated?: number;
        n_candidates?: number;
        trends?: Trend[];
      }>("/api/blog-dashboard/trends/refresh", kw ? { focus: kw } : {});
      const created = res.created ?? 0;
      const updated = res.updated ?? 0;
      const scanned = res.n_candidates ?? 0;
      toast.ok(
        `Radar${kw ? ` · “${kw}”` : ""}: ${created} new · ${updated} updated${
          scanned ? ` · ${scanned} scanned` : ""
        }`,
      );
      onChanged();
    } catch {
      toast.err(t.blog.trends.refreshFailed);
    } finally {
      setRefreshing(false);
    }
  };

  const setStatus = async (tr: Trend, status: "watch" | "write" | "dismissed") => {
    setBusyId(tr.id);
    try {
      await api.patch(`/api/blog-dashboard/trends/${tr.id}`, { status });
      toast.ok(status === "write" ? t.blog.trends.writePromoted : t.blog.trends.statusUpdated);
      onChanged();
    } catch (e) {
      const msg = e instanceof ApiError && e.status === 409 ? t.blog.trends.writeGateHint : t.blog.trends.statusFailed;
      toast.err(msg);
    } finally {
      setBusyId(null);
    }
  };

  const filters: Array<typeof filter> = ["all", "write", "watch", "dismissed"];

  return (
    <div>
      <div className="mb-4 space-y-3">
        <p className="text-sm text-text-2">{t.blog.trends.intro}</p>
        <div className="flex flex-wrap items-center gap-2">
          {/* Keyword-scoped radar: type a theme (e.g. "hydration") and the
              engine adds it to its trade-press queries. Empty = broad radar. */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-3" />
            <input
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && focus.trim() && !refreshing) refresh(focus);
              }}
              placeholder="Search trends by keyword (e.g. hydration)"
              disabled={refreshing}
              className="w-full rounded-control border border-border bg-surface-1 py-1.5 pl-8 pr-3 text-sm text-text placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-500/40 disabled:opacity-50"
            />
          </div>
          <button
            onClick={() => refresh(focus)}
            disabled={refreshing || !focus.trim()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-control bg-accent-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search trends
          </button>
          <button
            onClick={() => refresh()}
            disabled={refreshing}
            title="Broad radar sweep (no keyword)"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-border px-3 py-1.5 text-sm font-medium text-text-2 hover:bg-surface-3 disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            {refreshing ? t.blog.trends.refreshing : t.blog.trends.refresh}
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              "rounded-chip px-2.5 py-1 text-xs font-medium transition-colors",
              filter === f ? "bg-accent-500 text-white" : "bg-surface-2 text-text-2 hover:bg-surface-3"
            )}
          >
            {t.blog.trends.filter[f]}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={<Radar className="h-6 w-6" />} title={t.blog.trends.empty} hint={t.blog.trends.emptyHint} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {shown.map((tr) => (
            <TrendCard key={tr.id} trend={tr} busy={busyId === tr.id} onSet={(s) => setStatus(tr, s)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TrendCard({
  trend,
  busy,
  onSet,
}: {
  trend: Trend;
  busy: boolean;
  onSet: (status: "watch" | "write" | "dismissed") => void;
}) {
  const t = useT();
  const isWrite = trend.status === "write";
  return (
    <CardShell
      className="flex flex-col gap-2.5"
      accent={isWrite ? "border-l-success" : trend.status === "dismissed" ? "border-l-border" : "border-l-info"}
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={`/blog/trend/${trend.id}`} className="group min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Eyebrow>
              {trend.benefit_area ? benefitAreaLabel(trend.benefit_area) : t.blog.trends.notMapped}
            </Eyebrow>
            <TrendMatchBadge match={trend.claim_match} />
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 shrink-0 text-accent-500" />
            <h3 className="font-display text-base font-semibold text-text group-hover:text-accent-700 dark:group-hover:text-accent-300">
              {trend.term}
            </h3>
          </div>
        </Link>
        <span
          className={clsx(
            "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium",
            trend.status === "write"
              ? "bg-success-soft text-success"
              : trend.status === "dismissed"
                ? "bg-surface-3 text-text-3"
                : "bg-info-soft text-info"
          )}
        >
          {t.blog.trends.filter[trend.status as "watch" | "write" | "dismissed"]}
        </span>
      </div>

      <Link to={`/blog/trend/${trend.id}`} className="block space-y-1.5">
        {trend.headline && <p className="text-sm text-text-2">{trend.headline}</p>}
        {trend.framing_md && (
          <p className="line-clamp-3 text-xs leading-relaxed text-text-3">{trend.framing_md}</p>
        )}
      </Link>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-3">
        <CertOverlapBadges certs={trend.cert_overlap} />
        {trend.momentum_note && (
          <span className="inline-flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> {trend.momentum_note}
          </span>
        )}
        <span
          className={clsx(
            "rounded-chip px-1.5 py-0.5",
            trend.write_eligible ? "bg-success-soft text-success" : "bg-surface-2 text-text-3"
          )}
        >
          {trend.write_eligible ? t.blog.trends.groundable : t.blog.trends.writeGateHint}
        </span>
      </div>

      <div className="mt-auto flex items-center gap-1.5 pt-1">
        {isWrite && trend.promoted_topic_id ? (
          <Link
            to={`/blog/topic/${trend.promoted_topic_id}`}
            className="inline-flex items-center gap-1.5 rounded-control bg-accent-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-600"
          >
            {t.blog.trends.open} <ArrowRight className="h-4 w-4" />
          </Link>
        ) : (
          <button
            onClick={() => onSet("write")}
            disabled={busy || !trend.write_eligible}
            title={!trend.write_eligible ? t.blog.trends.writeGateHint : undefined}
            className="inline-flex items-center gap-1.5 rounded-control bg-accent-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PenLine className="h-4 w-4" /> {t.blog.trends.write}
          </button>
        )}
        {trend.status !== "watch" && (
          <button
            onClick={() => onSet("watch")}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-control border border-border px-2.5 py-1.5 text-sm text-text-2 hover:bg-surface-3 disabled:opacity-40"
          >
            <Eye className="h-4 w-4" /> {t.blog.trends.watch}
          </button>
        )}
        {trend.status !== "dismissed" && (
          <button
            onClick={() => onSet("dismissed")}
            disabled={busy}
            title={t.blog.trends.dismiss}
            className="inline-flex items-center gap-1 rounded-control border border-border px-2 py-1.5 text-sm text-text-3 hover:bg-surface-3 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-text-3" />}
      </div>
    </CardShell>
  );
}
